import { IdentificationMethod } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import DropdownBody from '@cobliteam/ui/components/Dropdown/components/DropdownBody';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

interface MultiSelectFaceIdIdentificationMethodProps {
  size?: 'sm' | 'md';
  selectedOptions: IdentificationMethod[];
  onChange: (identificationMethod: IdentificationMethod[]) => void;
}

const MultiSelectFaceIdIdentificationMethod = ({ selectedOptions, onChange, size = 'md' }: MultiSelectFaceIdIdentificationMethodProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);

  const getIdentificationMethodLabel = (identificationMethod: IdentificationMethod) => {
    switch (identificationMethod) {
      case IdentificationMethod.WITHOUT_IDENTIFICATION:
        return t('face_id_identification_methods.not_identified');
      case IdentificationMethod.FACE_IDENTIFICATION:
        return t('face_id_identification_methods.camera_identified');
      case IdentificationMethod.USER_CONFIRMATION:
        return t('face_id_identification_methods.user_confirmed');
      case IdentificationMethod.USER_EDIT:
        return t('face_id_identification_methods.user_identified');
      default:
        return t('face_id_identification_methods.no_image');
    }
  };

  const hasNotChanged = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
  }, [selectedOptions]);

  const handleClear = useCallback(() => {
    setDraft([]);
    onChange([]);
  }, [onChange]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (item: IdentificationMethod) => {
      const existItem = draft.includes(item);
      const newDraft = existItem ? draft.filter((value) => value !== item) : [...draft, item];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={handleClear}>
              {t('face_id_identification_methods.clear_selection')}
            </Button>
          </Dropdown.Header>

          <DropdownBody className=''>
            <Dropdown.List
              heightItem={28}
              heightList={300}
              data={Object.values(IdentificationMethod)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: IdentificationMethod) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={item}>{getIdentificationMethodLabel(item)}</Label>
                </div>
              )}
            />
          </DropdownBody>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('face_id_identification_methods.cancel')}
              </Button>
              <Button size='sm' full onClick={handleApply} disabled={hasNotChanged}>
                {t('face_id_identification_methods.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='smile-plus' color='primary' />}>
        {t('face_id_identification_methods.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectFaceIdIdentificationMethod;
